import call from "./call";

export interface RetrievalSource {
  document_name: string;
  page?: number;
  sheet_name?: string;
}

export interface RetrievalQuestionResult {
  question_id: number;
  question_text: string;
  sources: RetrievalSource[];
  raw_sources: string;
  sources_count: number;
}

export interface RetrievalCategoryResult {
  category_name: string;
  questions: Record<string, RetrievalQuestionResult>;
}

export interface RetrievalCategoryMetrics {
  total_questions: number;
  total_sources: number;
  avg_sources_per_question: number;
}

export interface RetrievalMetrics {
  total_questions: number;
  total_sources: number;
  avg_sources_per_question: number;
  unique_documents: number;
  per_category: Record<string, RetrievalCategoryMetrics>;
}

export interface RecallPerQuestion {
  category_key: string;
  question_key: string;
  question_id: number | null;
  recall: number;
  found_count: number;
  expected_count: number;
  all_found: boolean;
  found: RetrievalSource[];
  missing: RetrievalSource[];
  expected_sources: RetrievalSource[];
}

export interface RecallCategoryMetrics {
  total: number;
  passed: number;
  failed: number;
  avg_recall: number;
}

export interface RecallMetrics {
  total_evaluated: number;
  passed: number;
  failed: number;
  pass_pct: number;
  avg_recall: number;
  overall_recall: number;
  total_found_refs: number;
  total_expected_refs: number;
  per_category: Record<string, RecallCategoryMetrics>;
  per_question: RecallPerQuestion[];
}

export interface RetrievalTestStatusResponse {
  status: "queued" | "running" | "completed" | "error";
  progress: string;
  results: Record<string, RetrievalCategoryResult> | null;
  metrics: RetrievalMetrics | null;
  recall_metrics: RecallMetrics | null;
  report_md: string | null;
  config_used: Record<string, number | boolean | string>;
  error_message?: string;
}

export interface StartRetrievalTestResponse {
  test_id: string;
  status: string;
}

export const startRetrievalTest = async (
  companyId: number,
  questionListId: number,
  configOverrides: Record<string, number | boolean | string>,
  groundTruth?: Record<string, unknown> | null
): Promise<StartRetrievalTestResponse> => {
  const { data } = await call.post<StartRetrievalTestResponse>(
    "/v1/retrieval_test/run/",
    {
      company_id: companyId,
      question_list_id: questionListId,
      config_overrides: configOverrides,
      ...(groundTruth && { ground_truth: groundTruth }),
    },
    {
      snack: {
        error: "retrievalTest.startError",
      },
    }
  );
  return data;
};

export const getRetrievalTestStatus = async (
  testId: string
): Promise<RetrievalTestStatusResponse> => {
  const { data } = await call.get<RetrievalTestStatusResponse>(
    `/v1/retrieval_test/status/${testId}/`
  );
  return data;
};

// --- Grid Search ---

export interface GridSearchComboResult {
  config: Record<string, number | boolean | string>;
  metrics: RetrievalMetrics;
  recall_metrics: RecallMetrics | null;
}

export interface GridSearchStatusResponse {
  status: "queued" | "running" | "completed" | "error";
  progress: string;
  combo_results: GridSearchComboResult[] | null;
  report_md?: string | null;
  error_message?: string;
}

export const startGridSearch = async (
  companyId: number,
  questionListId: number,
  paramGrid: Record<string, (number | boolean | string)[]>,
  groundTruth?: Record<string, unknown> | null
): Promise<StartRetrievalTestResponse> => {
  const { data } = await call.post<StartRetrievalTestResponse>(
    "/v1/retrieval_test/grid_search/run/",
    {
      company_id: companyId,
      question_list_id: questionListId,
      param_grid: paramGrid,
      ...(groundTruth && { ground_truth: groundTruth }),
    },
    {
      snack: {
        error: "snack:retrievalTest.startError",
      },
    }
  );
  return data;
};

export const getGridSearchStatus = async (
  testId: string
): Promise<GridSearchStatusResponse> => {
  const { data } = await call.get<GridSearchStatusResponse>(
    `/v1/retrieval_test/status/${testId}/`
  );
  return data;
};
